import { cn } from '../../../lib/utils'
import { Skeleton } from '../../../components/ui/skeleton'
import type { Stage } from '../../../types'
import { LABEL_MIN, SLOT_MIN, SLOT_PX } from './timeUtils'
import { HourLabel } from './HourLabel'

const PLACEHOLDER_STAGE_COLS = 4
const TOTAL_HOURS = 10
const START_HOUR = 18

const SKELETON_BLOCKS: Array<{ col: number; startMin: number; endMin: number }> = [
  { col: 0, startMin: 60, endMin: 180 },
  { col: 1, startMin: 90, endMin: 240 },
  { col: 2, startMin: 30, endMin: 150 },
  { col: 3, startMin: 120, endMin: 300 },
  { col: 0, startMin: 240, endMin: 420 },
  { col: 2, startMin: 240, endMin: 360 },
  { col: 1, startMin: 360, endMin: 540 },
  { col: 3, startMin: 420, endMin: 600 },
]

export function ScheduleSkeleton({
  message,
  stages,
}: {
  message?: React.ReactNode
  stages?: Stage[]
}) {
  const totalSlots = (TOTAL_HOURS * 60) / SLOT_MIN
  const stageColCount = stages && stages.length > 0 ? stages.length : PLACEHOLDER_STAGE_COLS
  const stageCols = Array(stageColCount).fill('minmax(7rem, 1fr)').join(' ')
  const gtc = `1rem ${stageCols} var(--end-pad)`
  const gtr = `2rem repeat(${totalSlots}, ${SLOT_PX}px)`

  const toRowLine = (minutesFromStart: number) => 2 + minutesFromStart / SLOT_MIN

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div className="overflow-auto overscroll-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:[scrollbar-width:auto] md:[&::-webkit-scrollbar]:block -mx-4 -mb-4 md:mx-0 md:mb-0 md:border md:rounded-md h-full">
      <div
        className="w-full pb-1 [--end-pad:1rem] md:[--end-pad:0px]"
        style={{
          display: 'grid',
          gridTemplateColumns: gtc,
          gridTemplateRows: gtr,
          position: 'relative',
        }}
      >
        {/* Corner cell */}
        <div
          style={{ gridRow: 1, gridColumn: 1, position: 'sticky', top: 0, zIndex: 40 }}
          className="bg-background md:bg-muted md:border-b"
        />
        {/* End-pad backing cell (mobile) */}
        <div
          style={{ gridRow: 1, gridColumn: stageColCount + 2, position: 'sticky', top: 0, zIndex: 40 }}
          className="bg-background md:hidden"
        />
        {/* Backing cells behind first/last stage headers (mobile rounded-corner gap) */}
        <div style={{ gridRow: 1, gridColumn: 2, position: 'sticky', top: 0, zIndex: 39 }} className="bg-background md:hidden" />
        <div style={{ gridRow: 1, gridColumn: stageColCount + 1, position: 'sticky', top: 0, zIndex: 39 }} className="bg-background md:hidden" />
        {/* Stage headers */}
        {Array.from({ length: stageColCount }).map((_, i) => {
          const stage = stages?.[i]
          return (
            <div
              key={stage?.id ?? `stage-${i}`}
              style={{ gridRow: 1, gridColumn: i + 2, position: 'sticky', top: 0, zIndex: 40 }}
              className={cn(
                'flex items-center justify-center border-b bg-muted overflow-hidden min-w-0',
                i === 0 ? 'border-l rounded-tl-md md:rounded-none md:border-l-0' : 'border-l',
                i === stageColCount - 1 && 'border-r rounded-tr-md md:rounded-tr-none md:border-r-0',
              )}
            >
              {stage ? (
                <span className="text-xs font-semibold px-1 truncate select-none">
                  {stage.name}
                </span>
              ) : (
                <Skeleton className="h-3 w-12" />
              )}
            </div>
          )
        })}
        {/* Time labels */}
        {(() => {
          const cells = []
          for (let i = 0; i < TOTAL_HOURS * (60 / LABEL_MIN); i++) {
            const minutesFromStart = i * LABEL_MIN
            const isHour = minutesFromStart % 60 === 0 && minutesFromStart > 0
            if (!isHour) continue
            const rowStart = toRowLine(minutesFromStart)
            const rowEnd = toRowLine(Math.min(minutesFromStart + 60, TOTAL_HOURS * 60))
            cells.push(
              <div
                key={`tl-${i}`}
                style={{
                  gridColumn: 1,
                  gridRow: `${rowStart} / ${rowEnd}`,
                  position: 'sticky',
                  left: 0,
                  zIndex: 35,
                }}
                className="flex items-start justify-center pointer-events-none"
              >
                <HourLabel minutes={(START_HOUR * 60) + minutesFromStart} />
              </div>,
            )
          }
          return cells
        })()}
        {/* Gridlines */}
        {(() => {
          const lines = []
          for (let i = 0; i < TOTAL_HOURS * (60 / LABEL_MIN); i++) {
            const minutesFromStart = i * LABEL_MIN
            const rowLine = toRowLine(minutesFromStart)
            for (let j = 0; j < stageColCount; j++) {
              lines.push(
                <div
                  key={`hl-${i}-${j}`}
                  style={{
                    gridColumn: j + 2,
                    gridRow: `${rowLine} / ${rowLine + 1}`,
                    pointerEvents: 'none',
                  }}
                  className="border-t border-border/40"
                />,
              )
            }
          }
          return lines
        })()}
        {/* Skeleton blocks (only when no message) */}
        {!message &&
          SKELETON_BLOCKS.map((b, i) => (
            <Skeleton
              key={i}
              style={{
                gridColumn: b.col + 2,
                gridRow: `${toRowLine(b.startMin)} / ${toRowLine(b.endMin)}`,
              }}
              className="m-0.5 rounded"
            />
          ))}
      </div>
      </div>
      {message && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-4">
          <div className="rounded-lg bg-background/90 backdrop-blur-sm border px-4 py-3 pointer-events-auto">
            <p className="text-sm text-muted-foreground text-center">{message}</p>
          </div>
        </div>
      )}
    </div>
  )
}
